import type { IPublicClientApplication } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { useCallback } from 'react'
import { acquireInteraction, queueInteraction, releaseInteraction } from '../auth/interaction'
import { SignedOutReason, clearSignedOutReason, markSignedOut } from '../auth/signedOutReason'

/**
 * Single-flight sign-out — see docs/specs/7665-logout-interaction-in-progress/spec.md.
 *
 * MSAL permits one interactive API call at a time and rejects the second with
 * `interaction_in_progress`. CurvoX starts a sign-out from five independent places (the idle
 * timer, both Log Out buttons, the Apollo auth/error links, the "different account" button),
 * and the Apollo error link runs *per operation*, so a batch of requests resuming after an
 * idle period can collide with itself. Routing every caller through the shared interaction
 * lock is what makes that race impossible; the catches are the backstop for interaction state
 * MSAL may have carried over from an earlier interrupted redirect.
 */
export type LogoutOptions = {
  /** Recorded for the post-logout landing, which explains an inactivity sign-out to the user. */
  reason?: SignedOutReason
}

export function logoutOnce(instance: IPublicClientApplication, options: LogoutOptions = {}): void {
  // A silent renewal may hold the lock for a moment. Hand the logout to it rather than dropping
  // it — a user who clicked Log Out must end up signed out, not ignored (requirement 11).
  if (!acquireInteraction('logoutRedirect')) {
    queueInteraction(() => logoutOnce(instance, options))
    return
  }

  // Written before the redirect because the page leaves after it, so it has to be withdrawn if the
  // redirect fails. And it always describes the *latest* sign-out: an ordinary one clears whatever
  // an earlier failed attempt may have left, or the landing would blame inactivity (PR 7037 review).
  if (options.reason) {
    markSignedOut(options.reason)
  } else {
    clearSignedOutReason()
  }

  // Naming the account skips Entra's "Pick an account — which account do you want to sign out
  // of?" page. App.tsx sets the active account only on LOGIN_SUCCESS, so after a reload against
  // a cached session getActiveAccount() is null while getAllAccounts() is populated. With
  // neither, sign out unnamed — the account picker is a better outcome than no sign-out.
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined

  try {
    // The lock is deliberately NOT released on success: a successful redirect navigates away and
    // the lock dies with the page. Releasing here would reopen the race during teardown.
    Promise.resolve(instance.logoutRedirect({ account })).catch(error => {
      clearSignedOutReason()
      releaseInteraction()
      console.error('logoutRedirect failed:', error)
    })
  } catch (error) {
    // MSAL can fail synchronously or asynchronously depending on how early its guard trips.
    clearSignedOutReason()
    releaseInteraction()
    console.error('logoutRedirect threw:', error)
  }
}

/** Component-side handle on {@link logoutOnce}. */
export function useLogout(options: LogoutOptions = {}): () => void {
  const { instance } = useMsal()
  const { reason } = options
  return useCallback(() => logoutOnce(instance, { reason }), [instance, reason])
}
